import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import Swal from 'sweetalert2';

import { LoaderService } from '@teem/services/loader.service';
import { messages as i18n } from '@teem/constants/messages';
import { sortObjectKeys } from '@teem/helpers/functions';
import { getTerms } from '@teem/constants/proposal-terms';
import { UserService } from '@teem/services/user.service';
import { Proposal } from '@teem/models/proposal';

import { EditTermModalComponent } from '@teem/proposals-module/modals/edit-term-modal/edit-term-modal.component';

@Component({
  selector: 'edit-proposal-terms',
  templateUrl: './terms.component.html',
  styles: []
})
export class TermsComponent implements OnInit {
  @Input() proposal: Proposal;
	@Output() skipStep: EventEmitter<any> = new EventEmitter();
	@Output() triggerPrevious: EventEmitter<any> = new EventEmitter();
	@Output() onComplete: EventEmitter<any> = new EventEmitter();
	terms: Array<any> = getTerms();
	selectedTerms:{[k: string]: any}  = {};
	expendedTermGroups:any = {};

  constructor(
    private userService: UserService,
    private loaderService: LoaderService,
    private modalService: NgbModal) { }

  @Input('defaultTerms')
  set setDefaultTerms(defaultTerms:any) {
    /** 
     * Convert defaultTerms to given format e.g. 
     * selectedTerms = {
     *   <group-key>: {
     *     key: <group-key>,
     *     title: <group-title>
     *     terms : {
     *       <termkey1>: {
     *         key: <term-key>,
     *         title: <term-title>
     *         description: <term-desctiption>
     *       } 
     *     } 
     *   }
     * }
     */

    if(Object.keys(this.selectedTerms).length == 0 && defaultTerms) {
      let selectedTerms:any = {};
      let termGroup: any = {};

      for(termGroup of defaultTerms) {
        let terms:any = {};
        termGroup.terms.forEach(t => terms[t.key] = t);

        selectedTerms[termGroup.key] = {
          key: termGroup.key,
          title: termGroup.title,
          terms: terms 
        };

        // Expand term group from which terms are selected
        this.expendedTermGroups[termGroup.key] = true;
      }

      this.selectedTerms = selectedTerms;
    }
  }

  ngOnInit(): void {
    // Collapse/expend term group based on this.expendedTermGroups value
  	for(let group of this.terms) {
  		this.expendedTermGroups[group.key] = false;
  	}

    if(this.proposal ) {
      let proposalTerms = Object.values(this.proposal.terms);

      if(proposalTerms.length > 0) {
        let selectedTerms:any = {};
        let termGroup: any = {};

        for(termGroup of proposalTerms) {
          selectedTerms[termGroup.key] = termGroup;

          this.expendedTermGroups[termGroup.key] = true;
        }

        this.selectedTerms = selectedTerms;  
      }
    }

  }

  hasProperty(obj:any, key:string) {
  	try {
  		return obj[key] != undefined;	
  	} catch(err) {
  		return false;
  	}
  }

  getObjectValues(obj:any) {
  	return Object.values(obj);
  }

  toggleTermGroup(key:string) {
  	this.expendedTermGroups[key] = !this.expendedTermGroups[key];
  }

  addTerm(group: any, term: any) {
    let selectedTerms = this.selectedTerms ? Object.assign({}, this.selectedTerms) : {};

  	if(!this.hasProperty(selectedTerms, group.key))
  		selectedTerms[group.key] = {terms: {}}

  	selectedTerms[group.key].key = group.key;
		selectedTerms[group.key].title = group.title;

		if(!group.multipleSelect)
			selectedTerms[group.key].terms = {};

		selectedTerms[group.key].terms[term.key] = term;

    // Sort terms with respect to actual terms listing
    let groupTermsKeys = group.terms.map((term) => term.key);
    selectedTerms[group.key].terms = sortObjectKeys(selectedTerms[group.key].terms, groupTermsKeys);
    this.selectedTerms = sortObjectKeys(selectedTerms, Object.keys(this.expendedTermGroups));
  }

  removeTerm(groupKey: string, termKey: string) {
  	delete this.selectedTerms[groupKey].terms[termKey];

  	if( Object.keys(this.selectedTerms[groupKey].terms).length == 0 ) {
  		delete this.selectedTerms[groupKey];
  	}
  }

  editTermDescription(term: any, groupKey) {

    const modalRef = this.modalService.open(EditTermModalComponent);
    modalRef.componentInstance.term =  term;

    modalRef.result.then(result => {
      this.selectedTerms[groupKey].terms[term.key].description = result.description;
    }, fallbackResult => {
      // do nothing
    })
  }

  saveDefaultTerms() {
    if(Object.values(this.selectedTerms).length > 0) {
      this.loaderService.startLoading();

      let termValues = [];
      let termGroup:any;
      for(termGroup of Object.values(this.selectedTerms)) {
        termValues.push({
          'key':termGroup.key, 
          'terms': Object.values(termGroup.terms)
        })
      }

      this.userService.saveTerms({terms: termValues}).subscribe(data => {
        this.loaderService.stopLoading();

      }, err => {
        this.loaderService.stopLoading();
        
        Swal.fire(i18n.alert.error, i18n.getError(err), 'error');
      })
    } else {
      Swal.fire({
        title: i18n.proposal.please_select_terms,
        icon: 'warning'
      })
    }
  }

  submit() {
  	if(Object.values(this.selectedTerms).length > 0) {
      this.onComplete.emit(this.selectedTerms);
    } else {
      Swal.fire({
        title: i18n.proposal.please_select_terms,
        icon: 'warning'
      })
    }
  }
}
